/* @jsxImportSource @emotion/react */
import { css } from '@emotion/react/macro'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import req from '@sportninja/common/api/request'
import { getPlayerTypesForHockey } from '@sportninja/common/selectors/types'
import { ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { t } from '@sportninja/common/i18n'

import LoadingSpinner from '../../components/LoadingSpinner'
import { Flex } from '../../components/Layout'
import { font } from '../../components/css'
import Picture from '../../components/Picture'
import { goalieStats, skaterStats } from '../../components/Statistics'
import List from '../../components/List'
import { media } from '../../components/Responsive'

// dataList is an array
// it contains an object of objects (seasonMap)
// each object is an individual season/team pair to list in the table
const dataReducer = (dataList = []) => {
  let isSkater = false
  let isGoalie = false
  let data = []

  console.log('DATA LIST', dataList)

  dataList.forEach((seasonMap) => {
    const list = Object.keys(seasonMap).filter((key) => key !== 'played_as_skater' && key !== 'played_as_goalie')

    if (list.length > 0) {
      list.forEach((key) => {
        return data.push(seasonMap[key])
      })
    }
  })

  const existsSkater = dataList.find((d) => d.played_as_skater)
  const existsGoalie = dataList.find((d) => d.played_as_goalie)

  console.log(`DATA ${existsSkater ? 'SKATER' : 'GOALIE'}`, data)

  return {
    data,
    isSkater: !!existsSkater,
    isGoalie: !!existsGoalie
  }
}

export const Title = ({ children }) => {
  return (
    <div
      css={css`
        padding-bottom: 20px;
        margin-bottom: -8px;
        ${font.title}
        font-weight: 700;
        font-size: 24px;
        letter-spacing: 0.015em;
        text-transform: uppercase;
        border-bottom: 1px solid #3f4753;

        ${media.mobile} {
          padding-left: 12px;
        }
      `}
    >
      {children}
    </div>
  )
}

// When player is supplied, this is for a player. Otherwise, it's the user profile
const StatsBySeason = ({
  playerIds,
  isPublicRoute,
  setShowStats = () => {},
}) => {
  const [skaterData, setSkaterData] = useState([])
  const [goalieData, setGoalieData] = useState([])
  const [loading, setLoading] = useState(true)
  const [isSkater, setIsSkater] = useState(false)
  const [isGoalie, setIsGoalie] = useState(false)

  const useHasNoStatsYet = useCallback(
    () => !isSkater && !isGoalie,
    [isSkater, isGoalie]
  )

  useEffect(() => {
    if (!loading && useHasNoStatsYet()) {
      if (skaterData.length === 0 && goalieData.length === 0) {
        setShowStats(false)
      } else {
        setShowStats(true)
      }
    }
  }, [skaterData, goalieData, loading, useHasNoStatsYet])

  useEffect(() => {
    if (playerIds?.length > 0) {
      const skaterPromises = []
      const goaliePromises = []

      playerIds.forEach((playerId) => {
        skaterPromises.push(
          req(
            `${
              isPublicRoute ? '/public' : ''
            }/players/${playerId}/stats/canlan`,
            {
              query: { goalie: 0 },
            }
          )
        )
        goaliePromises.push(
          req(
            `${
              isPublicRoute ? '/public' : ''
            }/players/${playerId}/stats/canlan`,
            {
              query: { goalie: 1 },
            }
          )
        )
      })

      setLoading(true)
      Promise.all(skaterPromises)
        .then((results) => {
          const { data: skaterData } = dataReducer(results)
          setSkaterData(skaterData)
          if (skaterData.length > 0) {
            setIsSkater(true)
          }

          Promise.all(goaliePromises).then((goalies) => {
            const { data: goalieData } = dataReducer(goalies)
            setGoalieData(goalieData)
            if (goalieData.length > 0) {
              setIsGoalie(true)
            }
          })
        })
        .catch((error) => {
          setLoading(false)
          console.log('error', error)
        })
        .finally(() => {
          setLoading(false)
        })
    } else {
      setLoading(false)
    }
  }, [playerIds.length])

  const baseHeaders = [
    {
      text: t('common:competition'),
      sizePercent: 35,
      minWidth: 180,
      Cell: ({ item, isSorted }) => {
        let to
        if (item?.season?.id) {
          to = `${ROUTES.SCHEDULE_ROOT}/${item.season.id}/statistics`
          const queries = []
          if (item?.team?.id) {
            queries.push(`team_id=${item.team.id}`)
          }
          if (item?.isGoalie) {
            queries.push('is_goalie=1')
          }
          if (queries.length > 0) {
            to += `?${queries.join('&')}`
          }
        }

        return (
          <Flex
            alignItems='center'
            as={to ? Link : undefined}
            to={to}
            css={css`
              ${isSorted &&
              `
                  color: ${colors.DEFAULT_FLAIR};
                  display: -webkit-box;
                  -webkit-line-clamp: 2;
                  -webkit-box-orient: vertical;
                  overflow: hidden;
                `}
            `}
            title={item?.season?.name_full}
          >
            {item?.season?.name_full}
          </Flex>
        )
      },
    },
    {
      text: 'Team',
      sizePercent: 35,
      minWidth: 220,
      // sort: 'team.name',
      Cell: ({ item, isSorted }) => {
        let to
        if (item?.team?.id) {
          to = `${ROUTES.TEAM_ROOT}/${item.team.id}/statistics`
          const queries = []
          if (item?.season?.id) {
            queries.push(`schedule_id=${item.season.id}`)
          }
          if (item?.isGoalie) {
            queries.push('is_goalie=1')
          }
          if (queries.length > 0) {
            to += `?${queries.join('&')}`
          }
        }
        return (
          <Flex
            alignItems='center'
            as={to ? Link : undefined}
            to={to}
            css={css`
              font-size: 14px;
              ${isSorted && `color: ${colors.DEFAULT_FLAIR};`}
            `}
            title={item?.team?.name_full}
          >
            <Picture
              square
              fit
              size='xxsmall'
              imageId={getImageThumbnailId(item?.team)}
              iconName='user-friends'
            />
            <div
              css={css`
                margin-left: 8px;

                .team-name,
                .org-name {
                  display: -webkit-box;
                  -webkit-line-clamp: 2;
                  -webkit-box-orient: vertical;
                  overflow: hidden;
                }
              `}
            >
              <div className='team-name'>{item?.team?.name_full}</div>
              <div
                className='org-name'
                css={css`
                  margin-top: 2px;
                  font-size: 13px;
                  color: lightgray;
                `}
              >
                {item?.team?.organization?.name_full}
              </div>
            </div>
          </Flex>
        )
      },
    },
  ]

  const statMapper = (stat) => {
    const accessor = stat.accessor || stat.text
    return {
      text: stat.text,
      sizePercent: 6,
      minWidth: 60,
      sort: accessor,
      fontSize: 14,
      toolTip: stat.toolTip,
      accessor: (item) => {
        return `${item.stats.find((s) => s.abbr === accessor)?.value || 0}`
      },
    }
  }

  const headers = [...baseHeaders, ...skaterStats.map(statMapper)]
  const goalieHeaders = [...baseHeaders, ...goalieStats.map(statMapper)]

  return (
    <div
      className='stats-by-season'
      css={css`
        display: flex;
        flex-direction: column;
        min-height: 98px;
        margin-top: 24px;
        padding: 20px 16px;
        background-color: #27303e;
        border: 1px solid #3f4753;
        border-radius: 8px;

        ${media.mobile} {
          padding: 16px 0;
        }
      `}
    >
      {loading && <LoadingSpinner size={4} />}
      {/* {!loading && skaterData?.length === 0 && goalieData?.length === 0 && (
        <Flex alignItems='center' justifyContent='center'>
          There are no statistics available
        </Flex>
      )} */}
      {!loading && (isSkater || useHasNoStatsYet()) && (
        <>
          <Title>Player Stats</Title>
          <List
            forceMobile
            headerBorder
            headers={headers.filter((h) => typeof h !== 'undefined')}
            bgColor='#3F4753'
            listProps={{
              items: skaterData,
              getKey: (item) => `${item?.season?.id}-${item?.team?.id}`,
            }}
          />
        </>
      )}
      {!loading && (isGoalie || useHasNoStatsYet()) && (
        <div
          css={css`
            ${skaterData?.length > 0 && 'margin-top: 24px;'}
          `}
        >
          <Title>Goalie Stats</Title>
          <List
            forceMobile
            headerBorder
            headers={goalieHeaders.filter((h) => typeof h !== 'undefined')}
            bgColor='#3F4753'
            listProps={{
              items: goalieData,
              getKey: (item) => `${item?.season?.id}-${item?.team?.id}`,
            }}
          />
        </div>
      )}
    </div>
  )
}

const mapStateToProps = (state) => {
  return {
    playerTypes: getPlayerTypesForHockey(state),
  }
}

export default connect(mapStateToProps, null)(StatsBySeason)
